import React, { useState, useEffect } from 'react';
import { useMasterEvent } from '../../context/MasterEventContext';
import { scoringService } from '../../services/scoring';
import { teamsService } from '../../services/teams';
import { authService } from '../../services/auth';
import { activityDisplayName, isActivityHidden } from '../../services/activityVisibility';
import { isCompetitionActivity } from '../../services/activityTraits';
import Avatar from '../shared/Avatar';
import TeamTag from '../shared/TeamTag';
import HelpTip from '../shared/HelpTip';
import './ScoringDashboard.css';

function formatDate(dateStr) {
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

function UpcomingCompetition({ activity, masterEvent, canSeeObfuscatedDetails, getUserName, getUserAvatar }) {
  const isHidden = isActivityHidden(activity, masterEvent, canSeeObfuscatedDetails);
  // Activity-specific teams when configured, otherwise the event teams from Team Selection.
  const teams = activity.isTeamBased ? teamsService.getForActivity(activity.id) : [];
  const date = !isHidden && activity.date && formatDate(activity.date);
  const medals = ['🥇', '🥈', '🥉'];
  const points = (activity.pointsConfig || []).slice(0, 3);

  return (
    <div className="scoring-card scoring-card--activity">
      <div className="scoring-activity-header">
        <h4 className="scoring-activity-name">{activityDisplayName(activity, masterEvent, canSeeObfuscatedDetails)}</h4>
        <span className="scoring-activity-type">{activity.isTeamBased ? '👥 Teams' : '👤 Individual'}</span>
      </div>
      <div className="scoring-upcoming-meta">
        {!isHidden && <span>{activity.type}</span>}
        {date && <span>{date}</span>}
        {activity.status === 'draft' && <span className="scoring-upcoming-draft">draft</span>}
        {points.length > 0 && <span>{points.map((p) => `${medals[p.place - 1] || `#${p.place}`} ${p.points}`).join(' · ')} pts</span>}
      </div>
      {activity.isTeamBased && (teams.length === 0 ? (
        <p className="scoring-empty">No teams yet. Set them up in Team Selection.</p>
      ) : (
        <div className="scoring-upcoming-teams">
          {teams.map((team) => (
            <div key={team.id} className="scoring-upcoming-team">
              <span className="scoring-team-dot" style={{ background: team.color || '#888' }} />
              <span className="scoring-upcoming-team-name">{team.name}</span>
              <span className="scoring-upcoming-team-members">
                {(team.members || []).slice(0, 6).map((userId) => (
                  <span key={userId} title={getUserName(userId)}>{getUserAvatar(userId)}</span>
                ))}
                {(team.members || []).length > 6 && <span className="scoring-upcoming-more">+{team.members.length - 6}</span>}
                {(team.members || []).length === 0 && <span className="scoring-upcoming-more">No members</span>}
              </span>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

export default function ScoringDashboard() {
  const { masterEvent, activities, scores, isEventAdmin, isActivityAdmin } = useMasterEvent();
  const canSeeObfuscatedDetails = isEventAdmin || isActivityAdmin;
  const [leaderboard, setLeaderboard] = useState([]);
  const [teamLeaderboard, setTeamLeaderboard] = useState([]);
  const [teams, setTeams] = useState([]);
  const allUsers = authService.getAllUsers();

  useEffect(() => {
    if (masterEvent) {
      setLeaderboard(scoringService.getLeaderboard(masterEvent.id));
      setTeamLeaderboard(scoringService.getTeamLeaderboard(masterEvent.id));
      setTeams(teamsService.getAllForMasterEvent(masterEvent.id));
    }
  }, [masterEvent, scores]);

  const getUserName = (userId) => {
    const u = allUsers.find((usr) => usr.id === userId);
    return u?.displayName || 'Unknown';
  };

  const getUserAvatar = (userId) => {
    const u = allUsers.find((usr) => usr.id === userId);
    return <Avatar user={u} size={18} />;
  };

  const isScored = (act) => scores.some((s) => s.activityId === act.id);
  const concludedActivities = activities.filter((a) => a.status === 'concluded' && (isCompetitionActivity(a) || isScored(a)));
  const upcomingCompetitions = activities
    .filter((a) => a.status !== 'concluded' && isCompetitionActivity(a) && (a.status !== 'draft' || canSeeObfuscatedDetails))
    .sort((a, b) => {
      if (a.date && b.date) return new Date(a.date) - new Date(b.date);
      if (a.date) return -1;
      if (b.date) return 1;
      return (a.order || 0) - (b.order || 0);
    });

  return (
    <div className="scoring">
      <h2 className="scoring-title">Scores & Leaderboard</h2>

      {/* Overall Leaderboard */}
      <div className="scoring-card scoring-card--leaderboard">
        <h3 className="scoring-card-title">Overall Leaderboard<HelpTip label="Overall leaderboard">Points per player across all individual competitions. Points come from each activity's placement table once an admin scores it.</HelpTip></h3>
        {leaderboard.length === 0 ? (
          <p className="scoring-empty">No scores recorded yet. Scores will appear after activities are concluded and scored.</p>
        ) : (
          <table className="scoring-table">
            <thead>
              <tr>
                <th className="scoring-th scoring-th--rank">#</th>
                <th className="scoring-th">Player</th>
                <th className="scoring-th scoring-th--right">Activities</th>
                <th className="scoring-th scoring-th--right">Total Points</th>
              </tr>
            </thead>
            <tbody>
              {leaderboard.map((entry, idx) => {
                const medals = ['🥇', '🥈', '🥉'];
                return (
                  <tr key={entry.userId || entry.teamId} className="scoring-row">
                    <td className="scoring-td scoring-td--rank">
                      {medals[idx] || idx + 1}
                    </td>
                    <td className="scoring-td">
                      <div className="scoring-player">
                        <span className="scoring-player-avatar">{getUserAvatar(entry.userId)}</span>
                        <span className="scoring-player-name">{getUserName(entry.userId)}<TeamTag userId={entry.userId} /></span>
                      </div>
                    </td>
                    <td className="scoring-td scoring-td--right">
                      {entry.activities.length}
                    </td>
                    <td className="scoring-td scoring-td--right scoring-td--points">
                      {entry.totalPoints}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Team Leaderboard */}
      <div className="scoring-card scoring-card--leaderboard">
        <h3 className="scoring-card-title">Team Leaderboard<HelpTip label="Team leaderboard">Points per team across all team-based competitions. Admins can carry these points over as a starting balance in team betting.</HelpTip></h3>
        {teamLeaderboard.length === 0 ? (
          <p className="scoring-empty">No team scores recorded yet</p>
        ) : (
          <table className="scoring-table">
            <thead>
              <tr>
                <th className="scoring-th scoring-th--rank">#</th>
                <th className="scoring-th">Team</th>
                <th className="scoring-th">Members</th>
                <th className="scoring-th scoring-th--right">Activities</th>
                <th className="scoring-th scoring-th--right">Total Points</th>
              </tr>
            </thead>
            <tbody>
              {teamLeaderboard.map((entry, idx) => {
                const team = teams.find((t) => t.id === entry.teamId);
                const medals = ['🥇', '🥈', '🥉'];
                return (
                  <tr key={entry.teamId} className="scoring-row">
                    <td className="scoring-td scoring-td--rank">
                      {medals[idx] || idx + 1}
                    </td>
                    <td className="scoring-td">
                      <div className="scoring-player" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{
                          width: 10, height: 10, borderRadius: '50%',
                          background: team?.color || '#888', display: 'inline-block', flexShrink: 0,
                        }} />
                        <span className="scoring-player-name">{team?.name || entry.teamId}</span>
                      </div>
                    </td>
                    <td className="scoring-td">
                      <div style={{ display: 'flex', gap: 2 }}>
                        {(team?.members || []).slice(0, 4).map((userId) => (
                          <span key={userId} style={{ fontSize: 14 }} title={getUserName(userId)}>
                            {getUserAvatar(userId)}
                          </span>
                        ))}
                        {(team?.members || []).length > 4 && (
                          <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', alignSelf: 'center' }}>
                            +{team.members.length - 4}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="scoring-td scoring-td--right">
                      {entry.activities.length}
                    </td>
                    <td className="scoring-td scoring-td--right scoring-td--points">
                      {entry.totalPoints}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Upcoming competitions with the teams that will be scored */}
      <h3 className="scoring-breakdown-title">Upcoming Competitions<HelpTip label="Upcoming competitions">Activities marked as Competition that are not concluded yet. Team-based ones list the teams that will be scored; the medals show the points for each placement.</HelpTip></h3>
      {upcomingCompetitions.length === 0 ? (
        <div className="scoring-card">
          <p className="scoring-empty">No upcoming competitions. Mark an activity as a Competition to list it here.</p>
        </div>
      ) : (
        <div className="scoring-breakdown-grid">
          {upcomingCompetitions.map((act) => (
            <UpcomingCompetition
              key={act.id}
              activity={act}
              masterEvent={masterEvent}
              canSeeObfuscatedDetails={canSeeObfuscatedDetails}
              getUserName={getUserName}
              getUserAvatar={getUserAvatar}
            />
          ))}
        </div>
      )}

      {/* Per-Activity Breakdown */}
      <h3 className="scoring-breakdown-title">Activity Breakdown<HelpTip label="Activity breakdown">Placements and points for each concluded competition.</HelpTip></h3>
      {concludedActivities.length === 0 ? (
        <div className="scoring-card">
          <p className="scoring-empty">No concluded activities yet.</p>
        </div>
      ) : (
        <div className="scoring-breakdown-grid">
          {concludedActivities.map((act) => {
            const actScores = scores
              .filter((s) => s.activityId === act.id)
              .sort((a, b) => a.placement - b.placement);

            return (
              <div key={act.id} className="scoring-card scoring-card--activity">
                <div className="scoring-activity-header">
                  <h4 className="scoring-activity-name">{activityDisplayName(act, masterEvent, canSeeObfuscatedDetails)}</h4>
                  {!isActivityHidden(act, masterEvent, canSeeObfuscatedDetails) && <span className="scoring-activity-type">{act.type}</span>}
                </div>
                {actScores.length === 0 ? (
                  <p className="scoring-empty">Not scored yet.</p>
                ) : (
                  <div className="scoring-placements">
                    {actScores.map((s) => {
                      const medals = ['🥇', '🥈', '🥉'];
                      return (
                        <div key={s.id} className="scoring-placement-row">
                          <span className="scoring-placement-medal">
                            {medals[s.placement - 1] || `#${s.placement}`}
                          </span>
                          <span className="scoring-placement-avatar">{s.teamId ? '👥' : getUserAvatar(s.userId)}</span>
                          <span className="scoring-placement-name">{s.teamId ? (teams.find(t => t.id === s.teamId)?.name || s.teamId) : getUserName(s.userId)}{s.userId && <TeamTag userId={s.userId} />}</span>
                          <span className="scoring-placement-points">{s.points} pts</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
